import React, { useState } from 'react'
import moment from 'moment';
import { useStyle } from "./Style";
import Grid from "@material-ui/core/Grid";
import { TextValidator, SelectValidator, ValidatorForm } from 'react-material-ui-form-validator';
import MenuItem from '@mui/material/MenuItem';
import Button from "@mui/material/Button";
import { CircularProgress } from '@mui/material';

// Mthunzi's grace period is 30 days past due_date (MTHUNZI_RULE_SPEC.md, rule F4) - an event dated
// after that has genuinely lapsed and must not be claimable.
const MTHUNZI_GRACE_PERIOD_DAYS = 30;

const SelectPolicy = (props) => {
  if (!props.policies) {
    return
  }
  const classes = useStyle();
  const [policies, setPolicies] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [policy, setPolicy] = useState('');
  const [customerguid, setCustomerguid] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target

    if (name === "policies") {
      setPolicies(value)
      setPolicy(value)
      setCustomerguid(value.customer_guid)
      // Changing the policy invalidates any prior verification.
      if (props.resetEventQuote) props.resetEventQuote();
    }
    if (name === "eventDate") {
      setEventDate(value)
      // Changing the event date invalidates any prior verification.
      if (props.resetEventQuote) props.resetEventQuote();
    }
  }


  // A policy whose quotation has never been swapped (upgrade/downgrade) keeps its coverage
  // window open-ended (end_date stays at a '9999-01-01' placeholder forever), so the backend's
  // event-lookup always reports the event as "found" no matter how long ago the policy actually
  // lapsed. Reject client-side first, using the policy's own due_date + grace period, so a
  // lapsed-and-never-since-paid policy can't let the claimant proceed on that false positive.
  const isPastGracePeriod = (selectedPolicy, selectedEventDate) => {
    if (!selectedPolicy || !selectedEventDate || !selectedPolicy.due_date) return false;
    // A real (non-placeholder) end_date reflects an actual coverage boundary (e.g. a quotation
    // swap) and takes precedence over the due-date+grace approximation.
    if (selectedPolicy.end_date && selectedPolicy.end_date !== '9999-01-01') {
      return moment(selectedEventDate).isAfter(moment(selectedPolicy.end_date));
    }
    const graceEnd = moment(selectedPolicy.due_date).add(MTHUNZI_GRACE_PERIOD_DAYS, 'days');
    return moment(selectedEventDate).isAfter(graceEnd);
  };

  // Non_Payment means the lapse cron has already run (policy-administration-complex's
  // lapseOutstandingInArrearsPoliciesTask, 120+ days past due_date with no further payment) - no
  // claim against this policy should be initiated from here regardless of the event date, so
  // reject unconditionally rather than relying on the due-date-plus-grace math above to reach the
  // same conclusion.
  const isNonPayment = (selectedPolicy) => selectedPolicy && selectedPolicy.status === 'Non_Payment';

  const handlePolicies = () => {
    if (isNonPayment(policy)) {
      props.rejectEventQuote('Event NOT found', 'POLICY_NON_PAYMENT');
      return;
    }
    if (isPastGracePeriod(policy, eventDate)) {
      props.rejectEventQuote(
        "Event occurred after the policy's due date plus its grace period - the policy had lapsed by this event date and it cannot be claimed against.",
        'EVENT_AFTER_DUE_DATE_GRACE_PERIOD',
      );
      return;
    }
    const payload = {
      "customer_guid": customerguid,
      "policy_guid": policy.guid,
      "event_date": eventDate,
    }
    console.log("select policy ", payload)
    props.handleSelectPolicies(payload);
    const tempClaimInitiation = props.claimInitiationPayload;
    tempClaimInitiation["event_date"] = eventDate;
    tempClaimInitiation["policy_number"] = policy.policy_number;
    props.setClaimInitiationPayload(tempClaimInitiation);
    // handleReset();
  }
  // Deliberately NOT filtering to policy.active === true here - an inactive/lapsed policy
  // should still be selectable so the agent can attempt the claim and get a clear rejection
  // (isNonPayment/isPastGracePeriod above, or a specific reason from the backend), rather than
  // the policy silently disappearing from this dropdown with no explanation of why a known
  // policy number isn't there. This mirrors the same show-it-and-let-the-real-check-reject
  // approach already used for benefit selection in SelectMember.js.
  const filteredPolicies = props.policies.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  // GET /api/quotations/event now returns a specific `detail` (and machine-readable `reason`)
  // once the lookup fails, derived server-side from the actual policy/quotation state - prefer
  // that, since it's authoritative. Only fall back to a local approximation (using the
  // already-selected policy's own status/dates) if the backend hasn't been updated yet or
  // couldn't determine one, so this degrades gracefully rather than requiring both changes to
  // land together.
  const buildEventErrorMessage = (eventQuoteError, selectedPolicy, selectedEventDate) => {
    if (eventQuoteError && typeof eventQuoteError === 'object' && eventQuoteError.detail) {
      return eventQuoteError.detail;
    }
    if (!selectedPolicy || !selectedEventDate) return 'Event NOT found';
    if (selectedPolicy.status === 'Cancelled') {
      return 'This policy has been cancelled - the event cannot be claimed against it.';
    }
    if (['Non_Payment', 'InArrears', 'Unpaid'].includes(selectedPolicy.status)) {
      return 'This policy has lapsed due to non-payment.';
    }
    if (selectedPolicy.status === 'Expired') {
      return 'This policy has expired.';
    }
    if (selectedPolicy.start_date && selectedEventDate < selectedPolicy.start_date) {
      return 'Event occurred before the policy start date - it is not covered.';
    }
    const coverageEnd = selectedPolicy.end_date || selectedPolicy.due_date;
    if (coverageEnd && coverageEnd !== '9999-01-01' && selectedEventDate > coverageEnd) {
      return "Event occurred after the policy's coverage period.";
    }
    return 'No coverage found for this event date. Please verify the policy and date, or contact support.';
  };

  return (
    <>
      <ValidatorForm onSubmit={() => handlePolicies()}>
        <Grid container spacing={2} className={classes.selectpolicyWrapper}>
          <Grid item xs={12} md={6} sm={6}>
            <SelectValidator
              labelId="select-policy"
              id="select-policy"
              value={policies}
              name='policies'
              type="text"
              label="Select Policy"
              onChange={handleChange}
              variant="outlined"
              validators={['required']}
              errorMessages={['Please select a policy']}
              style={{ width: "100%" }}
              sx={{
                fontFamily: "Open Sans",
                fontStyle: "normal",
                fontWeight: 300,
                fontSize: "12px",
                lineHeight: "16px",
                letterSpacing: "0.005em",
                color: "#000000",
              }}
            >

              {
                filteredPolicies.map((policy, index) => {
                  return (
                    <MenuItem
                      key={index}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }} value={policy}>{policy.policy_number}</MenuItem>
                  )

                })
              }


            </SelectValidator>
          </Grid>
          <Grid item xs={12} md={6} sm={6}>
            <TextValidator
              id="event-date"
              label="Select Event Date"
              name='eventDate'
              value={eventDate}
              onChange={handleChange}
              style={{ width: "100%" }}
              type='date'
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              validators={['required',`maxNumber:${new Date().toISOString().slice(0, 10)}`]}
              errorMessages={['Please enter an event date','Select a past date  ']}
              helperText="This is the date the incident (death or hospital admission) took place, not today's date."
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} sm={12} className={classes.claimBtn}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              type='submit'
            >
              {props.eventQuoteLoader ? <CircularProgress /> :'Verify event date' }
            </Button>
            {props.eventQuoteSuccess && <p style={{color: 'green'}} >Event found</p>}
            {props.eventQuoteError && <p style={{color: 'red'}} >{buildEventErrorMessage(props.eventQuoteError, policy, eventDate)}</p>}
          </Grid>
        </Grid>
      </ValidatorForm>
    </>
  )
}

export default SelectPolicy